export interface ParsedStatusRow {
  email: string;
  status?: string;
  // Реквизиты, применяемые бэкендом только при переходе в approved/validated.
  // level — уровень (MSc/PhD); сам не пишется, нужен бэку лишь чтобы различить одноимённые
  // программы магистратуры и аспирантуры (Life Sciences / Petroleum Engineering) без direction.
  level?: string;
  program?: string;
  direction?: string;
}

// Допустимые заголовки колонок (в нижнем регистре): помимо коротких имён принимаем подписи из
// выгрузки админки (напр. колонка уровня экспортируется как «Program Level»). Первый алиас —
// канонический, чтобы при совпадении нескольких выбирался ожидаемый столбец.
const EMAIL_ALIASES = ["email"];
const STATUS_ALIASES = ["status"];
const LEVEL_ALIASES = ["level", "program level", "program_level", "prog_level", "prog level"];
// Программа: бэк матчит значение по educational_program (из неё выводит prog_title). Реальные выгрузки
// озаглавливают колонку «Program Title», поэтому принимаем и её, и educational_program.
const PROGRAM_ALIASES = [
  "program", "program title", "program_title", "prog_title", "educational program", "educational_program",
];
// Одна колонка направления, как бы она ни называлась («Research area» / «Educational track» / direction):
// бэк сам выбирает educational_track (MSc) или research_area (PhD) по уровню заявки и матчит по ЗНАЧЕНИЮ,
// поэтому единый столбец работает для обоих уровней. Если вдруг в файле есть оба столбца — берём первый
// по этому порядку (на практике выгрузка содержит ровно один столбец направления).
const DIRECTION_ALIASES = ["direction", "research area", "research_area", "educational track", "educational_track"];

function findHeaderIndex(headerCells: string[], aliases: string[]): number {
  for (const alias of aliases) {
    const idx = headerCells.indexOf(alias);
    if (idx >= 0) {
      return idx;
    }
  }
  return -1;
}

export interface ParseStatusCsvInvalid {
  line: number;
  value: string;
}

export interface ParseStatusCsvResult {
  rows: ParsedStatusRow[];
  invalid: ParseStatusCsvInvalid[];
}

/**
 * Парсер CSV для импорта смены статуса по email.
 *
 * Намеренно без внешних зависимостей: вход — email и короткий тег/имя статуса, так что
 * сложного экранирования не требуется. Авторитетная валидация email и дедупликация
 * выполняется на бэкенде (см. BulkStatusByEmailService). Здесь только структурный разбор:
 * - срезается BOM, поддерживаются переводы строк \r\n / \r / \n;
 * - делимитер автоопределяется (',' / ';' / табуляция);
 * - если в первой непустой строке есть колонка "email" — берём колонки по заголовку
 *   (email + опционально status / level / program / direction; level принимает и подпись
 *   «Program Level» из выгрузки админки), иначе позиционно:
 *   1-я колонка = email, 2-я = status, 3-я = program, 4-я = direction (level — только по заголовку);
 * - level / program / direction применяются бэкендом только при переходе в approved/validated;
 * - строки без email попадают в `invalid` (с 1-индексным номером строки в файле).
 */
export function parseStatusCsv(text: string): ParseStatusCsvResult {
  const rows: ParsedStatusRow[] = [];
  const invalid: ParseStatusCsvInvalid[] = [];

  const clean = (text || "").replace(/^﻿/, "");
  const rawLines = clean.split(/\r\n|\r|\n/);

  let delimiter = ",";
  let emailIdx = 0;
  let statusIdx = 1;
  let programIdx = 2;
  let directionIdx = 3;
  // level не имеет позиционного дефолта (−1): без заголовка существующие файлы
  // (email,status,program,direction) не должны вдруг трактовать 3-ю колонку как уровень.
  let levelIdx = -1;
  let headerLineIndex = -1;

  // Определяем делимитер и наличие заголовка по первой непустой строке.
  for (let i = 0; i < rawLines.length; i++) {
    if (rawLines[i].trim() === "") {
      continue;
    }

    delimiter = detectDelimiter(rawLines[i]);
    const headerCells = splitCsvLine(rawLines[i], delimiter).map(c => c.toLowerCase());

    if (headerCells.includes("email")) {
      emailIdx = findHeaderIndex(headerCells, EMAIL_ALIASES);
      statusIdx = findHeaderIndex(headerCells, STATUS_ALIASES); // -1, если колонки status нет
      levelIdx = findHeaderIndex(headerCells, LEVEL_ALIASES); // -1, если нет (в т.ч. «Program Level»)
      programIdx = findHeaderIndex(headerCells, PROGRAM_ALIASES); // -1, если нет
      directionIdx = findHeaderIndex(headerCells, DIRECTION_ALIASES); // -1, если нет
      headerLineIndex = i;
    }

    break;
  }

  for (let i = 0; i < rawLines.length; i++) {
    if (i === headerLineIndex || rawLines[i].trim() === "") {
      continue;
    }

    const cells = splitCsvLine(rawLines[i], delimiter);
    const email = (cells[emailIdx] ?? "").trim();
    const status = statusIdx >= 0 ? (cells[statusIdx] ?? "").trim() : "";
    const level = levelIdx >= 0 ? (cells[levelIdx] ?? "").trim() : "";
    const program = programIdx >= 0 ? (cells[programIdx] ?? "").trim() : "";
    const direction = directionIdx >= 0 ? (cells[directionIdx] ?? "").trim() : "";

    if (!email) {
      invalid.push({line: i + 1, value: rawLines[i]});
      continue;
    }

    const row: ParsedStatusRow = {email};
    if (status) {
      row.status = status;
    }
    if (level) {
      row.level = level;
    }
    if (program) {
      row.program = program;
    }
    if (direction) {
      row.direction = direction;
    }

    rows.push(row);
  }

  return {rows, invalid};
}

function detectDelimiter(line: string): string {
  const candidates = [",", ";", "\t"];
  let best = ",";
  let bestCount = -1;

  for (const candidate of candidates) {
    const count = line.split(candidate).length - 1;
    if (count > bestCount) {
      bestCount = count;
      best = candidate;
    }
  }

  return best;
}

function splitCsvLine(line: string, delimiter: string): string[] {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];

    if (inQuotes) {
      if (ch === "\"") {
        if (line[i + 1] === "\"") {
          current += "\"";
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === "\"") {
      inQuotes = true;
    } else if (ch === delimiter) {
      cells.push(current);
      current = "";
    } else {
      current += ch;
    }
  }

  cells.push(current);

  return cells.map(c => c.trim());
}

export default parseStatusCsv;
